import React, { useMemo } from 'react'
import { NameDto } from '@/api/Common/Types'
import { FormFieldLayout } from '@/components/Form/FormField'
import { MultiSelect } from '@/components/Select'

const ANY_STATUS_VALUE = 'any'
const STATUS_VALUE = 'status'

export interface NotificationProductValue {
  id: number
  name: string
  statuses: NameDto[]
}

interface NotificationProductStatusFieldProps {
  name: string
  label: React.ReactNode
  value: NotificationProductValue[]
  products: NameDto<number | string>[]
  statuses: NameDto[]
  readOnly?: boolean
  onChange(value: NotificationProductValue[]): void
}

const getAnyStatusKey = (productId: number | string) => `product:${productId}:${ANY_STATUS_VALUE}`

const getStatusKey = (productId: number | string, statusId: number | string) =>
  `product:${productId}:${STATUS_VALUE}:${statusId}`

const parseSelectionKey = (value: unknown) => {
  if (typeof value !== 'string') return null

  const [prefix, productId, type, statusId] = value.split(':')
  const parsedProductId = Number(productId)

  if (prefix !== 'product' || !Number.isFinite(parsedProductId)) return null

  if (type === ANY_STATUS_VALUE) {
    return { productId: parsedProductId, isAnyStatus: true, statusId: null }
  }

  const parsedStatusId = Number(statusId)
  if (type !== STATUS_VALUE || !Number.isFinite(parsedStatusId)) return null

  return { productId: parsedProductId, isAnyStatus: false, statusId: parsedStatusId }
}

const toSelectionKeys = (value: unknown) =>
  Array.isArray(value) ? value.map(String) : value != null ? [String(value)] : []

const getSelectedKeys = (products: NotificationProductValue[]) =>
  products.flatMap((product) =>
    product.statuses.length
      ? product.statuses.map((status) => getStatusKey(product.id, status.id))
      : [getAnyStatusKey(product.id)]
  )

const getStatusOptions = (statuses: NameDto[], selectedProducts: NotificationProductValue[]) => {
  const statusMap = new Map<number, NameDto>()

  statuses.forEach((status) => statusMap.set(Number(status.id), status))
  selectedProducts.forEach((product) => {
    product.statuses.forEach((status) => statusMap.set(Number(status.id), status))
  })

  return [...statusMap.values()].map((status) => ({
    id: Number(status.id),
    name: status.name,
  }))
}

export const NotificationProductStatusField: React.FC<NotificationProductStatusFieldProps> = ({
  name,
  label,
  value,
  products,
  statuses,
  readOnly,
  onChange,
}) => {
  const statusOptions = useMemo(() => getStatusOptions(statuses, value), [statuses, value])
  const selectedKeys = useMemo(() => getSelectedKeys(value), [value])
  const selectedProductIds = useMemo(
    () => new Set(value.map((product) => Number(product.id))),
    [value]
  )

  const options = useMemo(
    () =>
      products.flatMap((product) => {
        const productOption = {
          value: getAnyStatusKey(product.id),
          label: (
            <span className='flex min-w-0 flex-1 items-center gap-2'>
              <span className='min-w-0 flex-1 truncate font-semibold'>{product.name}</span>
              <span className='shrink-0 text-xs font-normal text-muted-foreground'>Any status</span>
            </span>
          ),
          text: `${product.name} Any status`,
        }

        if (!selectedProductIds.has(Number(product.id))) {
          return [productOption]
        }

        return [
          productOption,
          ...statusOptions.map((status) => ({
            value: getStatusKey(product.id, status.id),
            label: <span className='pl-4'>{status.name}</span>,
            text: `${product.name} ${status.name}`,
          })),
        ]
      }),
    [products, selectedProductIds, statusOptions]
  )

  const getSelectionLabel = (selectedValue: unknown) => {
    const parsed = parseSelectionKey(selectedValue)
    if (!parsed) return ''

    const productName =
      products.find((product) => Number(product.id) === parsed.productId)?.name || 'Product'
    const statusName = parsed.isAnyStatus
      ? 'Any status'
      : statusOptions.find((status) => Number(status.id) === parsed.statusId)?.name || 'Status'

    return `${productName}: ${statusName}`
  }

  const handleChange = (nextValue: unknown) => {
    const nextKeys = toSelectionKeys(nextValue)
    const previousKeys = new Set(selectedKeys)
    const keysByProduct = nextKeys.reduce<Record<number, string[]>>((acc, key) => {
      const parsed = parseSelectionKey(key)
      if (!parsed) return acc

      acc[parsed.productId] = [...(acc[parsed.productId] || []), key]
      return acc
    }, {})

    onChange(
      products.flatMap((product) => {
        const productId = Number(product.id)
        const productKeys = keysByProduct[productId] || []
        if (!productKeys.length) return []

        const anyStatusKey = getAnyStatusKey(product.id)
        const hasAnyStatus = productKeys.includes(anyStatusKey)
        const selectedStatusIds = productKeys
          .map((key) => parseSelectionKey(key)?.statusId)
          .filter((statusId): statusId is number => Number.isFinite(statusId))

        const anyStatusWasJustSelected = hasAnyStatus && !previousKeys.has(anyStatusKey)
        const nextStatuses =
          hasAnyStatus && (anyStatusWasJustSelected || !selectedStatusIds.length)
            ? []
            : statusOptions.filter((status) => selectedStatusIds.includes(Number(status.id)))

        return [
          {
            id: productId,
            name: product.name,
            statuses: nextStatuses,
          },
        ]
      })
    )
  }

  return (
    <FormFieldLayout label={label} name={name}>
      <MultiSelect
        id={name}
        name={name}
        value={selectedKeys}
        placeholder='Products and statuses'
        readOnly={readOnly}
        allowClear
        showSearch
        fullWidth
        selectionSummaryThreshold={0}
        renderSelectionSummary={(selectedOptions) => {
          if (selectedOptions.length === 1) {
            return getSelectionLabel(selectedOptions[0].value)
          }

          return `${selectedOptions.length} product/status filters`
        }}
        options={options}
        onChange={handleChange}
      />
    </FormFieldLayout>
  )
}
